import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { MARK_DATA_URL } from "@/assets/mark";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function BrandMark({ onClick }) {
  return (
    <Link to="/" onClick={onClick} data-testid="brand-logo" className="flex items-center gap-2.5 group">
      <img src={MARK_DATA_URL} alt="MinuteSwap" data-testid="brand-mark-img" className="h-8 w-8 rounded-md" />
      <span className="flex flex-col leading-none">
        <span className="font-head text-lg font-extrabold tracking-tight text-white">MinuteSwap</span>
        <span className="font-mono2 text-[9px] uppercase tracking-[0.28em] text-[var(--amber)]">Action Engine</span>
      </span>
    </Link>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const authed = user && typeof user === "object";

  const navLinks = authed
    ? [
        { to: "/app", label: "Action Engine" },
        { to: "/moves", label: "My Moves" },
        { to: "/impact", label: "Impact" },
      ]
    : [
        { to: "/", label: "Home" },
        { to: "/#demo", label: "20s Demo" },
      ];

  const go = (to) => { setOpen(false); if (to.startsWith("/#")) { navigate("/"); setTimeout(() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" }), 120); } else navigate(to); };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#08090A]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <BrandMark />

        <nav className="hidden items-center gap-7 md:flex">
          {navLinks.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => go(l.to)}
              className={`font-mono2 text-xs uppercase tracking-wider transition-colors hover:text-white ${location.pathname === l.to ? "text-white" : "text-neutral-400"}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {authed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-white/12 bg-[var(--surface-raised)] px-3 py-1.5 text-sm text-white hover:border-white/25">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--amber)] text-xs font-bold text-black">
                    {(user.first_name || user.name || "U")[0].toUpperCase()}
                  </span>
                  <span className="max-w-[120px] truncate">{user.first_name || user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 border-white/12 bg-[var(--surface-overlay)] text-white">
                <DropdownMenuItem data-testid="menu-impact" onClick={() => navigate("/impact")}>Impact Dashboard</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-moves" onClick={() => navigate("/moves")}>My Moves</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-context" onClick={() => navigate("/onboarding")}>Business context</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>Admin Control</DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="header-login-btn" variant="ghost" className="text-neutral-300 hover:text-white" onClick={() => navigate("/login")}>Log in</Button>
              <Button data-testid="header-cta-btn" className="btn-amber rounded-full px-5" onClick={() => navigate("/register")}>Get My Next Move</Button>
            </>
          )}
        </div>

        <button data-testid="mobile-menu-btn" className="text-white md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[#0E1013] px-5 py-4 md:hidden">
          <div className="flex flex-col gap-3">
            {navLinks.map((l) => (
              <button key={l.label} onClick={() => go(l.to)} className="text-left font-mono2 text-xs uppercase tracking-wider text-neutral-300">{l.label}</button>
            ))}
            <div className="mt-2 flex flex-col gap-2">
              {authed ? (
                <>
                  {user.role === "admin" && <Button variant="outline" onClick={() => go("/admin")} className="border-white/15">Admin</Button>}
                  <Button className="btn-amber" onClick={async () => { await logout(); go("/"); }}>Log out</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="border-white/15 text-white" onClick={() => go("/login")}>Log in</Button>
                  <Button className="btn-amber" onClick={() => go("/register")}>Get My Next Move</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
